/**
 * Drawing an effect's passes onto the canvas.
 *
 * The other half of effects/plan.ts: the press walk draws the same passes into
 * SVG and PDF, and this draws them onto the screen. Vector passes are plain
 * fills and strokes. A halo uses the browser's own shadow blur - which the
 * canvas specification defines as a Gaussian of half the blur value, the same
 * softness the press file rasterises - cast from a copy drawn far off the
 * canvas, so that only the shadow lands where the halo belongs.
 *
 * Everything here is in millimetres: the caller has already scaled the context
 * so that one unit is one millimetre of the layer.
 */

import type { Colour, Fill } from '../model/types';
import { isGradient } from '../model/types';
import { toCss } from '../model/units';
import type { Pass } from '../effects/plan';
import type { Matrix } from '../text/place';

export interface CanvasOutline {
  path: Path2D;
  m: Matrix;
}

/** Where the off-canvas copy is drawn, in device pixels. Far enough that no canvas reaches it. */
const AWAY = 30000;

/** Paths are expensive to parse and a glyph's outline never changes, so each is parsed once. */
const parsed = new Map<string, Path2D>();

export function pathFor( d: string ): Path2D {
  let path = parsed.get( d );

  if ( ! path ) {
    path = new Path2D( d );

    // A card has a few hundred distinct glyphs at most; this only guards
    // against a session that has typed in every font there is.
    if ( parsed.size > 4000 ) parsed.clear();

    parsed.set( d, path );
  }

  return path;
}

/**
 * Draws passes.
 *
 * @param only Which passes: those behind the object, the object itself, or
 *   all of them. A shape draws its own body through Konva, which already knows
 *   how to fill a gradient, and asks for the others here.
 * @param letters Whether the outlines are glyphs, whose strokes are round-joined.
 */
export function paintPasses(
  ctx: CanvasRenderingContext2D,
  passes: Pass[],
  outlines: CanvasOutline[],
  options: { only?: 'effects' | 'all'; letters?: boolean; line?: number } = {}
): void {
  for ( const pass of passes ) {
    if ( options.only === 'effects' && pass.body ) continue;

    if ( pass.source === 'box' && pass.box ) {
      box( ctx, pass );
      continue;
    }

    if ( pass.blur > 0 ) {
      halo( ctx, pass, outlines, options.line );
      continue;
    }

    vector( ctx, pass, outlines, !! options.letters );
  }
}

function box( ctx: CanvasRenderingContext2D, pass: Pass ): void {
  const b = pass.box;

  if ( ! b || ! pass.fill ) return;

  ctx.save();
  ctx.globalAlpha *= pass.alpha;
  ctx.fillStyle = cssOf( pass.fill );
  ctx.beginPath();

  const r = Math.max( 0, Math.min( b.r, b.w / 2, b.h / 2 ) );

  ctx.roundRect( b.x + pass.dx, b.y + pass.dy, b.w, b.h, r );
  ctx.fill();
  ctx.restore();
}

function vector( ctx: CanvasRenderingContext2D, pass: Pass, outlines: CanvasOutline[], letters: boolean ): void {
  ctx.save();
  ctx.globalAlpha *= pass.alpha;

  if ( pass.fill ) ctx.fillStyle = cssOf( pass.fill );

  if ( pass.stroke ) {
    ctx.strokeStyle = toCss( pass.stroke );
    ctx.lineJoin = letters || ! pass.body ? 'round' : 'miter';
  }

  for ( let n = 0; n < pass.repeat; n++ ) {
    for ( const outline of outlines ) {
      ctx.save();
      ctx.translate( pass.dx, pass.dy );
      ctx.transform( ...outline.m );

      if ( pass.fill ) ctx.fill( outline.path );

      if ( pass.stroke && pass.width > 0 ) {
        // A width is millimetres of the layer; inside a glyph's own space a
        // millimetre is many units, so it is scaled back to match.
        const scale = scaleOf( outline.m );

        ctx.lineWidth = pass.width / scale;

        if ( pass.dash?.length ) ctx.setLineDash( pass.dash.map( ( d ) => d / scale ) );

        ctx.stroke( outline.path );
      }

      ctx.restore();
    }
  }

  ctx.restore();
}

/**
 * A halo: the outline's shadow and nothing else.
 *
 * The outline is drawn a long way to the left in device space and its shadow
 * offset back by the same distance, so the shadow lands in place while the
 * outline itself is somewhere no one will see. The offset is worked out in
 * device pixels because that is the space a canvas shadow lives in - it
 * ignores the transform - which is also why this works whatever the layer's
 * rotation or flip.
 */
function halo( ctx: CanvasRenderingContext2D, pass: Pass, outlines: CanvasOutline[], line?: number ): void {
  const colour = pass.fill ? firstColourOf( pass.fill ) : pass.stroke;

  if ( ! colour ) return;

  const t = ctx.getTransform();
  const deviceScale = Math.sqrt( Math.abs( t.a * t.d - t.b * t.c ) );

  // The halo's own offset, carried into device space.
  const offsetX = t.a * pass.dx + t.c * pass.dy;
  const offsetY = t.b * pass.dx + t.d * pass.dy;

  ctx.save();
  ctx.setTransform( new DOMMatrix( [ 1, 0, 0, 1, -AWAY, 0 ] ).multiply( t ) );
  ctx.shadowColor = withAlpha( toCss( colour ), pass.alpha );
  ctx.shadowBlur = 2 * pass.blur * deviceScale;
  ctx.shadowOffsetX = AWAY + offsetX;
  ctx.shadowOffsetY = offsetY;
  ctx.fillStyle = '#000';
  ctx.strokeStyle = '#000';

  for ( let n = 0; n < pass.repeat; n++ ) {
    for ( const outline of outlines ) {
      ctx.save();
      ctx.transform( ...outline.m );

      if ( line ) {
        ctx.lineWidth = line / scaleOf( outline.m );
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke( outline.path );
      } else {
        ctx.fill( outline.path );
      }

      ctx.restore();
    }
  }

  ctx.restore();
}

/* ---------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

function scaleOf( m: Matrix ): number {
  return Math.sqrt( Math.abs( m[ 0 ] * m[ 3 ] - m[ 1 ] * m[ 2 ] ) ) || 1;
}

function firstColourOf( fill: Fill ): Colour | null {
  if ( ! isGradient( fill ) ) return fill;

  return fill.stops[ 0 ]?.colour ?? null;
}

/** A pass's fill as a CSS colour. A gradient pass is only ever the body, which Konva draws. */
function cssOf( fill: Fill ): string {
  const colour = firstColourOf( fill );

  return colour ? toCss( colour ) : '#000';
}

/** rgb(r, g, b) with an alpha, for a shadow colour. */
function withAlpha( css: string, alpha: number ): string {
  const match = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec( css );

  if ( match ) return `rgba(${ match[ 1 ] }, ${ match[ 2 ] }, ${ match[ 3 ] }, ${ alpha })`;

  const hex = /^#([0-9a-f]{6})$/i.exec( css );

  if ( hex ) {
    const v = parseInt( hex[ 1 ] as string, 16 );

    return `rgba(${ ( v >> 16 ) & 255 }, ${ ( v >> 8 ) & 255 }, ${ v & 255 }, ${ alpha })`;
  }

  return css;
}
